import { AriaDatapoint, VerbosePerfEventType } from './datapoints/AriaDatapoint';
import { getInitializeAnalyticsPromse } from './initializeAnalytics';
import optionallyTraceDatapoint from './utils/optionallyTraceDatapoint';
import { AWTPiiKind } from '@aria/webjs-sdk';
import { safeStringify, validatePropertyBag } from './utils/validatePropertyBag';
import isDevEnvironment from './utils/isDevEnvironment';
import getFlightControl from './utils/getFlightControl';
import shouldLogDatapoint from './utils/shouldLogDatapoint';
import { addCommonProperties } from './utils/logSessionDatapoint';
import { isFeatureEnabled } from 'owa-feature-flags';
import { DatapointOptions } from 'owa-analytics-actions';
import { createEventProperties, getLogger } from './AriaWrapper';
import { AnalyticsOptions, DataPointEventType } from './types/DatapointEnums';
import { getThroughEdge, getDag, getServerVersion } from 'owa-config';

export default function logDatapoint(datapoint: AriaDatapoint, overrideEventType?: string): void {
    getInitializeAnalyticsPromse().then(analyticsOptions => {
        let isFullVerboseLoggingEnabled = isFeatureEnabled('an-full-logging');

        let eventType: string = getEventType(datapoint, analyticsOptions, overrideEventType);

        let flightControl = getFlightControl(datapoint, analyticsOptions, eventType);

        if (
            !shouldLogDatapoint(datapoint, analyticsOptions, eventType) &&
            !isFullVerboseLoggingEnabled
        ) {
            return;
        }

        optionallyTraceDatapoint(datapoint);

        let loggerTokens = getTenantLoggers(analyticsOptions, datapoint.options);

        if (eventType == DataPointEventType.ClientQos) {
            datapoint.properties.ThroughAFD = getThroughEdge();
            datapoint.properties.ServiceVersion = getServerVersion();
            datapoint.properties.Dag = getDag();
        }

        for (let i = 0; i < loggerTokens.length; i++) {
            const e = createEventProperties(
                isFullVerboseLoggingEnabled ? VerbosePerfEventType : eventType
            );
            if (isFullVerboseLoggingEnabled) {
                e.properties['OriginalEvent'] = eventType;
            }

            const keys = Object.keys(datapoint.properties);
            for (let ii = 0; ii < keys.length; ii++) {
                e.properties[keys[ii]] = datapoint.properties[keys[ii]];
            }

            addCommonProperties(e, analyticsOptions.isEdu);
            e.properties['Sampled'] = flightControl?.rate ? flightControl.rate : 100;

            if (datapoint.propertyBag) {
                e.properties['MiscData'] = validatePropertyBag(datapoint);
            }
            if (datapoint.piiBag) {
                e.properties['PiiData'] = {
                    value: safeStringify(datapoint.piiBag),
                    pii: AWTPiiKind.GenericData,
                };
            }

            // let's not actually log datapoints if we are gulping or in a branch or a tds box
            // unless the datapoint is explicitly whitelisted
            if (
                !isDevEnvironment(analyticsOptions) ||
                eventType == DataPointEventType.ClientEventDevOnly
            ) {
                // we need to create a copy of the datapoint for each event, because there is a bug
                // in the aria code which will force the events to be sent to the same tenant if
                // the event is the same instance
                getLogger(loggerTokens[i]).logEvent(e);
            }
        }
    });
}

function getEventType(
    datapoint: AriaDatapoint,
    analyticsOptions: AnalyticsOptions,
    overrideEventType: string
): string {
    if (overrideEventType) {
        return overrideEventType;
    }

    if (datapoint.options) {
        if (datapoint.options.isVerbose) {
            return DataPointEventType.ClientVerbose;
        }

        if (datapoint.options.isCosmosDatapoint) {
            return DataPointEventType.ClientCosmos;
        }
    }

    if (
        analyticsOptions.qosDatapointNames != undefined &&
        analyticsOptions.qosDatapointNames.indexOf(datapoint.eventName) > -1
    ) {
        return DataPointEventType.ClientQos;
    }

    return DataPointEventType.ClientEvent;
}

function getTenantLoggers(analyticsOptions: AnalyticsOptions, options: DatapointOptions): string[] {
    return options?.tenantTokens ? options.tenantTokens : analyticsOptions.ariaTenantTokens;
}
